import * as React from 'react';

import { cn } from 'lib/utils';

/**
 * El marco de una pantalla del panel: contenedor, ancho, aire, título y subtítulo.
 *
 * Nace de un recuento hecho el 28/09/26 sobre las 14 pantallas de chirigota, y el problema no
 * era que alguna estuviera mal: era que **no había un patrón, había tres**. Cinco pantallas
 * llevaban el contenedor completo, cinco tenían contenedor pero sin el aire de arriba, y cuatro
 * no tenían contenedor. Y **siete tratamientos distintos del `<h1>`** — las que se desviaban
 * perdían `font-heading` y caían a la fuente del cuerpo, así que no cambiaba solo el tamaño:
 * cambiaba la tipografía, y por eso unas pantallas «se sentían» de otra aplicación.
 *
 * 🔴 El canon ya estaba escrito en `Foundations/Spacing` desde antes, con la inconsistencia
 * señalada por su nombre. No sirvió de nada: la documentación no obliga. Este componente existe
 * para que el marco deje de ser algo que cada pantalla acierta copiando de otra.
 *
 * NO es `ScreenShell`, que resuelve el *chrome* (sidebar y barras) y pone el margen contra los
 * bordes. Se usan juntos: `ScreenShell` decide qué chrome se ve y dónde empieza la página,
 * `PageHeader` cómo se enmarca el contenido. Hasta la 0.10.0 `ScreenShell` decía que no limitaba
 * el ancho «porque cada pantalla ya trae su propio contenedor» — ese contenedor a mano es justo
 * lo que este componente viene a que nadie vuelva a escribir.
 */
export interface PageHeaderProps {
	/**
	 * El único `<h1>` de la pantalla, y coincide con la etiqueta del menú (decisión de Marisa,
	 * 28/09/26): quien llega tiene que poder confirmar dónde está sin traducir nombres.
	 */
	title: React.ReactNode;
	/**
	 * Obligatorio a propósito. Si fuera opcional, la próxima pantalla volvería a salir sin él —
	 * que es exactamente lo que pasó con el aire de arriba, documentado y omitido cinco veces.
	 *
	 * Es `ReactNode` porque los buenos son dinámicos: «Tu plan {nombre} · actualizado el {fecha}».
	 *
	 * `NonNullable` porque `React.ReactNode` incluye `undefined` y `null`: sin esto, obligar solo
	 * impedía OMITIR la prop, y `subtitle={undefined}` compilaba y pintaba un párrafo vacío. La
	 * barandilla se saltaba con once caracteres.
	 */
	subtitle: NonNullable<React.ReactNode>;
	/**
	 * Una línea pequeña ENCIMA del título. Opcional.
	 *
	 * Existe porque las dos homes lo usan de verdad para saludar («Hola 👋») y sin ella no
	 * podrían adoptar este componente. No es un hueco genérico para meter cosas: va en
	 * `text-sm text-muted-foreground` y no es un encabezado, para que no compita con el `h1`.
	 */
	eyebrow?: React.ReactNode;
	/** A la derecha del título. Opcional, pero la usan 11 de las 14 pantallas reales. */
	action?: React.ReactNode;
	/**
	 * `default` para casi todo, `narrow` para formularios y lectura larga, `full` para tablas de
	 * muchas columnas.
	 *
	 * Son tres nombres y no cuatro clases a mano porque antes convivían `2xl`, `4xl`, `5xl` y
	 * `6xl` sin que nadie supiera por qué cada pantalla tenía el suyo. Una pantalla que necesite
	 * un ancho menor DENTRO de su marco lo limita por dentro, no cambiando el marco: así la
	 * cabecera sigue alineada con las demás y no «salta» al navegar.
	 *
	 * 🔴 `default` es `6xl`, no `5xl`. Se documentó `5xl` diciendo que era «el ancho de las
	 * pantallas bien resueltas» y el recuento no lo sostiene: en chirigota hay seis `6xl` contra
	 * dos `5xl`, y aquí `5xl` se usaba una sola vez. Con `5xl` por defecto, adoptar el componente
	 * habría estrechado seis pantallas 128 px sin que nadie lo hubiera pedido.
	 */
	width?: 'default' | 'narrow' | 'full';
	className?: string;
	children?: React.ReactNode;
}

const ANCHOS = {
	default: 'max-w-6xl',
	narrow: 'max-w-2xl',
	full: ''
} as const;

/**
 * 🔴 El margen LATERAL no lo pone este componente, a propósito. Lo pone el layout de la página.
 *
 * Se consideró al revés —que lo pusiera aquí, para que el componente fuese autónomo— y las dos
 * opciones funcionan; lo que las separa es qué pasa MIENTRAS se adopta:
 *
 *   1. La adopción no puede ser atómica. En chirigota son 14 pantallas y siete esperan copy de
 *      Producto para su subtítulo, así que van a convivir convertidas y sin convertir durante
 *      semanas. Si el margen sale del layout, cada pantalla todavía sin convertir se queda
 *      pegada al borde hasta que le toque. Dejándolo en el layout, una pantalla sin convertir se
 *      ve exactamente como hoy.
 *   2. `p-4` son los cuatro lados de una sola declaración, en el archivo que comparten las 14
 *      pantallas (`Dashboard.tsx`). En escritorio es además el único relleno de abajo —el
 *      `pb-20` es solo de móvil—, así que sacar de ahí la mitad lateral obliga a reescribirlo
 *      como `py-4` justo en el sitio donde un error se nota en todas a la vez.
 *
 * Y los fallos no son simétricos: si una pantalla se olvida de quitar SU margen, se ven 32 px en
 * vez de 16 —feo y visible—; al revés, una pantalla sin convertir pierde el hueco entero.
 *
 * ⚠️ Lo que NO es razón para esto, aunque lo parezca: que haya cosas fuera de la pantalla que
 * necesiten el hueco. El `ContactDataBanner` de chirigota se pinta fuera del contenido y dentro
 * de este mismo contenedor, pero lo CANCELA con `-mx-4` porque está pensado para llegar a los
 * bordes. Es el único que hay, así que ese argumento no tiene ningún caso que lo sostenga.
 *
 * Lo que sí es de aquí: el ancho máximo, el aire de arriba y el ritmo vertical.
 */
const MARCO = 'mx-auto w-full space-y-8 pt-12 lg:pt-14';

export const PageHeader = ({
	title,
	subtitle,
	eyebrow,
	action,
	width = 'default',
	className,
	children
}: PageHeaderProps) => (
	<div className={cn(MARCO, ANCHOS[width], className)}>
		{/* 🔴 Hasta `lg` la acción se APILA debajo, y no es un detalle de gusto.
		    Compartiendo fila a 320 px al título le quedan ~120 px contra un botón de ~150, y un
		    título real como «Laura, tienes 18.420,50 € ahorrados» no tiene dónde partir: `18.420,50`
		    mide ~160 px de una pieza y se sale de su caja. Esto se coló porque las capturas de
		    pantalla completa se toman a 1280 px y ninguna red lo mira.

		    ⚠️ El corte es `lg` y no `sm`, y costó una segunda medición. La acción va en `shrink-0`
		    —lo necesita, o un botón se aplastaría—, así que un `flex-wrap` DENTRO de ella queda
		    inerte: nunca se estrecha, luego nunca envuelve. Con el título en `min-w-0`, quien cede
		    es el título, hasta CERO. Medido en Chromium con tres botones y 456 px de contenido
		    —una admin en un iPad apaisado con el menú abierto—: el `h1` quedaba en `0 × 297` px,
		    una letra por línea, y la página entera ganaba 56 px de scroll horizontal. Con `lg`, el
		    mismo caso da 456 px de título y cero desbordamiento.

		    `items-start` y no `items-center` desde `lg`: el título puede ocupar dos líneas y la
		    acción tiene que quedarse arriba, no centrarse contra un bloque alto. */}
		<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-4">
			<div className="min-w-0">
				{eyebrow && <p className="text-sm text-muted-foreground mb-1">{eyebrow}</p>}
				{/* `break-words` como segunda red: el apilado le devuelve el ancho entero al
				    título, pero un importe largo en una pantalla de 320 px se sigue saliendo si
				    nadie le deja partir. */}
				{/* 🔴 `font-bold` y `tracking-[-0.03em]`, no `font-medium`, y el motivo importa.
				    El canon se sacó el 28/09/26 de las dos pantallas de chirigota que estaban bien
				    resueltas… leyendo sus CLASES, que decían `font-medium`. En pantalla nunca se
				    vieron así: en aquel repo una regla de ámbito por elemento (`<scope> h1 { …
				    font-weight: 700; letter-spacing: -0.03em }`) tiene más especificidad que
				    cualquier utilidad, y las pintaba a 700 y apretadas. O sea que el canon
				    describía una cosa y la referencia era otra, y al unificar por las clases todo
				    convergió al aspecto MÁS FLOJO.
				    Lo vio Marisa comparando dos pantallas en la 0.10.0: «hemos igualado para el
				    titular feo». Esto recupera lo que de verdad se tomó como referencia.

				    🔴 `text-2xl` añadido por debajo de `sm` (6 oct 2026, apretando Screens/Resumen
				    Multicuenta en small mobile): no cambia el peso/tracking que decidió Marisa, ni
				    el tamaño de `lg:` — solo el escalón de abajo, que antes saltaba directo de
				    móvil a escritorio sin ningún intermedio. A 320-374px, `text-3xl` en un título
				    real de dos palabras largas ("Tus planes de pensiones") envolvía a 2 líneas y
				    se comía más alto de lo que un titular necesita ahí. Afecta a las 5 Screens por
				    igual, igual que el arreglo del padding del decorator de Storybook. */}
				<h1 className="text-2xl sm:text-3xl lg:text-[40px] font-bold tracking-[-0.03em] font-heading leading-[1.1] text-foreground break-words">
					{title}
				</h1>
				<p className="text-base text-muted-foreground mt-1">{subtitle}</p>
			</div>
			{/* `self-start`: apilada, sin esto el botón se estiraría a todo el ancho. */}
			{action && <div className="self-start shrink-0">{action}</div>}
		</div>
		{children}
	</div>
);
